import { useRef, useState } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";
import { toast } from "sonner";

import { adminApi } from "@/lib/api";
import { AdminInput } from "@/components/admin/AdminUI";

const MAX_SIZE = 5 * 1024 * 1024;

export default function ImageUploadField({
  value,
  onChange,
  category = "general",
  placeholder = "Image URL or uploaded file",
  preview = true,
  className = "",
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const uploadFile = async (file) => {
    if (!file) return;

    if (!file.type?.startsWith("image/")) {
      toast.error("Please upload an image file.");
      return;
    }

    if (file.size > MAX_SIZE) {
      toast.error("Image too large. Max 5MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      setUploading(true);
      try {
        const dataUrl = reader.result;
        const saved = await adminApi.createMedia({
          filename: file.name,
          url: dataUrl,
          mime_type: file.type || "application/octet-stream",
          size: file.size,
          category,
        });
        onChange?.(saved.url || dataUrl);
        toast.success("Image uploaded");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Image upload failed");
      } finally {
        setUploading(false);
      }
    };
    reader.onerror = () => toast.error("Could not read this image.");
    reader.readAsDataURL(file);
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex flex-col sm:flex-row gap-2">
        <AdminInput
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
        />
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
          className="hidden"
          onChange={(e) => {
            uploadFile(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
        <button
          type="button"
          className="btn-outline shrink-0 justify-center px-3 py-2.5 text-[10px]"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
          Upload
        </button>
        {value ? (
          <button
            type="button"
            className="h-10 w-10 shrink-0 grid place-items-center border border-white/10 rounded-sm text-brand-gray hover:text-brand-red"
            onClick={() => onChange?.("")}
            aria-label="Clear image"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      {preview && value ? (
        <div className="aspect-[16/9] max-h-44 overflow-hidden rounded-sm border border-white/10 bg-ink-graphite">
          <img src={value} alt="Preview" className="h-full w-full object-cover" />
        </div>
      ) : null}
    </div>
  );
}
